import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SHORTS } from '../data/shortsData';
import ShortScene from '../components/ShortScene';
import { YouTubeShortPlayer, extractYtId } from '../components/YouTubeShortPlayer';
import { API } from '../lib/api';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ChevronUp, ChevronDown, BookOpen, Pause, Play, Clapperboard, Volume2, VolumeX, Plus, Users, Share2, Eye } from 'lucide-react';

const STEP_MS = 6500; // 4 étapes ≈ 26 s par short

const fmtViews = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1).replace('.0', '')} k` : `${n}`);

export default function ShortsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [idx, setIdx] = useState(() => {
    const i = SHORTS.findIndex((s) => s.id === searchParams.get('s'));
    return i >= 0 ? i : 0;
  });
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0); // 0-1 de l'étape courante
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem('cv_shorts_sound') === '0');
  const [feed, setFeed] = useState(SHORTS);
  const [views, setViews] = useState({});
  const [quizOn, setQuizOn] = useState(false);
  const [quizSel, setQuizSel] = useState(null);
  const [uiVisible, setUiVisible] = useState(true);
  const hideTimer = useRef(null);
  const seenRef = useRef(new Set());
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const raf = useRef(null);
  const startRef = useRef(Date.now());
  const pausedAt = useRef(0);
  const touchY = useRef(null);
  const short = feed[idx];
  const isVideo = short.type === 'video';
  const isYoutube = short.type === 'youtube';
  const ytVideoId = isYoutube ? (short.ytId || extractYtId(short.embedSrc)) : null;
  const ytControllable = !!ytVideoId;
  const nSteps = short.steps.length;

  // URL d'embed de secours (Vimeo, Dailymotion) : autoplay muet
  const embedUrl = (s) => {
    const sep = s.embedSrc.includes('?') ? '&' : '?';
    if (s.embedSrc.includes('vimeo')) return `${s.embedSrc}${sep}autoplay=1&muted=1&playsinline=1`;
    return `${s.embedSrc}${sep}autoplay=1&mute=1&playsinline=1`;
  };

  // Shorts communautaires validés : fichiers (mp4) + liens avec embed (YouTube, Vimeo, Dailymotion)
  useEffect(() => {
    axios.get(`${API}/shorts/community`).then((r) => {
      const community = (r.data || []).map((s) => {
        const base = {
          id: s.id,
          theme: s.theme,
          kind: `Communauté · par ${s.author_name}`,
          title: s.title,
          community: true,
          course_to: '/shorts/proposer',
          color: '#0ea5e9',
          steps: [s.description || 'Short proposé par la communauté CodeVoiture.'],
        };
        if (s.source_type === 'file' && s.media_url) {
          return { ...base, type: 'video', src: `${API.replace('/api', '')}${s.media_url}` };
        }
        if (s.source_type === 'link' && s.embed_url) {
          return { ...base, type: 'youtube', embedSrc: s.embed_url };
        }
        if (s.source_type === 'link' && s.video_url) {
          return { ...base, type: 'external', platform: s.platform, url: s.video_url };
        }
        return null;
      }).filter(Boolean);
      if (community.length) setFeed([...SHORTS, ...community]);
    }).catch(() => {});
  }, []);

  // Compteur de vues : charge les totaux + comptabilise 1 vue par short vu dans la session
  useEffect(() => {
    axios.get(`${API}/shorts/views`).then((r) => setViews(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => {
    const id = short?.id;
    if (!id || seenRef.current.has(id)) return;
    seenRef.current.add(id);
    axios.post(`${API}/shorts/view`, { short_id: id }).catch(() => {});
    setViews((v) => ({ ...v, [id]: (v[id] || 0) + 1 }));
  }, [short?.id]);

  // Lien profond partagé : /shorts?s={id}
  useEffect(() => {
    const target = searchParams.get('s');
    if (!target) return;
    const i = feed.findIndex((s) => s.id === target);
    if (i >= 0 && i !== idx) {
      setIdx(i);
      setStep(0);
      setProgress(0);
      startRef.current = Date.now();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, searchParams]);

  const share = async () => {
    const shareUrl = `${window.location.origin}/shorts?s=${short.id}`;
    const payload = { title: `CodeVoiture · ${short.title}`, text: `${short.kind} — ${short.title}`, url: shareUrl };
    try {
      if (navigator.share) {
        await navigator.share(payload);
        return;
      }
      throw new Error('no-share');
    } catch (e) {
      if (e.name === 'AbortError') return;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Lien copié ! Envoie-le à tes amis 🔗');
      } catch {
        toast.error('Impossible de copier le lien');
      }
    }
  };

  const goShort = useCallback((n) => {
    setIdx(((n % feed.length) + feed.length) % feed.length);
    setStep(0);
    setProgress(0);
    setQuizOn(false);
    setQuizSel(null);
    setPaused(false);
    if (localStorage.getItem('cv_shorts_sound') !== '0') setMuted(false);
    startRef.current = Date.now();
  }, [feed.length]);

  const goStep = useCallback((n) => {
    if (n < 0) { goShort(idx - 1); return; }
    if (n >= nSteps) {
      if (short.quiz && !quizOn) { setQuizOn(true); return; }
      goShort(idx + 1);
      return;
    }
    setStep(n);
    setProgress(0);
    startRef.current = Date.now();
  }, [idx, nSteps, goShort, short, quizOn]);

  // Masquage auto de l'interface pendant la lecture, retour au toucher
  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setUiVisible(false), 3500);
  }, []);
  const revealUi = useCallback(() => { setUiVisible(true); scheduleHide(); }, [scheduleHide]);
  useEffect(() => {
    setUiVisible(true);
    scheduleHide();
    return () => clearTimeout(hideTimer.current);
  }, [idx, step, scheduleHide]);
  useEffect(() => {
    if (paused || quizOn) { clearTimeout(hideTimer.current); setUiVisible(true); }
    else scheduleHide();
  }, [paused, quizOn, scheduleHide]);
  const uiCls = `transition-opacity duration-500 ${uiVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`;

  // Son automatique : applique la préférence aux mp4, repli muet si le navigateur bloque l'autoplay sonore
  useEffect(() => {
    const v = videoRef.current;
    if (!isVideo || !v) return;
    v.muted = muted;
    v.play().catch(() => {
      if (!muted) { v.muted = true; setMuted(true); v.play().catch(() => {}); }
    });
  }, [idx, isVideo, muted]);

  // Son automatique YouTube/fin de vidéo : gérés par YouTubeShortPlayer (API officielle)

  // Moteur de lecture (shorts animés uniquement — vidéos, YouTube et liens externes gèrent leur propre temps)
  useEffect(() => {
    if (isVideo || isYoutube || short.type === 'external' || quizOn) return undefined;
    const tick = () => {
      if (!paused) {
        const el = Date.now() - startRef.current;
        const p = Math.min(1, el / STEP_MS);
        setProgress(p);
        if (p >= 1) {
          if (step + 1 >= nSteps) {
            if (short.quiz) setQuizOn(true);
            else goShort(idx + 1);
          } else { setStep(step + 1); setProgress(0); startRef.current = Date.now(); }
        }
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [paused, step, nSteps, idx, goShort, isVideo, quizOn, short]);

  const togglePause = () => {
    if (isYoutube) {
      if (!ytControllable) return;
      setPaused(!paused);
      return;
    }
    if (isVideo && videoRef.current) {
      if (videoRef.current.paused) { videoRef.current.play(); setPaused(false); }
      else { videoRef.current.pause(); setPaused(true); }
      return;
    }
    if (!paused) pausedAt.current = Date.now();
    else startRef.current += Date.now() - pausedAt.current;
    setPaused(!paused);
  };

  // Molette + clavier (desktop)
  useEffect(() => {
    const onWheel = (e) => { if (Math.abs(e.deltaY) > 30) goShort(idx + (e.deltaY > 0 ? 1 : -1)); };
    const onKey = (e) => {
      if (e.key === 'ArrowDown') goShort(idx + 1);
      if (e.key === 'ArrowUp') goShort(idx - 1);
      if (e.key === 'ArrowRight') goStep(step + 1);
      if (e.key === 'ArrowLeft') goStep(step - 1);
      if (e.key === ' ') { e.preventDefault(); togglePause(); }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('wheel', onWheel);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('wheel', onWheel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, step, paused]);

  // Swipe vertical (mobile)
  const onTouchStart = (e) => { touchY.current = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    if (touchY.current === null) return;
    const dy = e.changedTouches[0].clientY - touchY.current;
    if (dy < -60) goShort(idx + 1);
    else if (dy > 60) goShort(idx - 1);
    touchY.current = null;
  };

  return (
    <div className="bg-black min-h-[100dvh] md:min-h-screen md:flex md:items-center md:justify-center md:py-8" data-testid="shorts-page">
      {/* Cadre format téléphone sur desktop, plein écran sur mobile */}
      <div
        className="relative w-full h-[calc(100dvh-68px)] md:h-[82vh] md:max-h-[820px] md:w-auto md:aspect-[9/16] md:rounded-3xl md:border md:border-white/15 overflow-hidden select-none"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="shorts-player"
      >
        {/* Scène animée, vidéo ou YouTube */}
        <div className="absolute inset-0">
          {isYoutube && ytVideoId ? (
            <YouTubeShortPlayer
              key={short.id}
              videoId={ytVideoId}
              muted={muted}
              paused={paused}
              onEnded={() => { if (short.quiz && !quizOn) { setQuizOn(true); setProgress(1); } else goShort(idx + 1); }}
            />
          ) : isYoutube ? (
            <iframe
              key={short.id}
              ref={iframeRef}
              src={embedUrl(short)}
              title={short.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="shorts-embed"
            />
          ) : short.type === 'external' ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-5 px-8 text-center bg-gradient-to-br from-[#0e1420] via-[#0a0a0b] to-[#160a14]" data-testid="shorts-external">
              <span className="px-3 py-1 rounded-full bg-white/[0.07] border border-white/20 text-white/70 text-[10.5px] font-bold uppercase tracking-[0.2em] font-mono-spec">
                Vidéo {short.platform}
              </span>
              <h2 className="font-serif-editorial text-white text-[24px] leading-snug">{short.title}</h2>
              <a href={short.url} target="_blank" rel="noopener noreferrer" data-testid="shorts-external-link"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white text-black text-[14px] font-bold active:scale-95 transition shadow-xl">
                <Play size={16} /> Regarder sur {short.platform} ↗
              </a>
              <p className="text-white/40 text-[11.5px] leading-snug max-w-[260px]">
                Cette vidéo se lit sur sa plateforme d'origine — sélectionnée et validée par l'équipe CodeVoiture.
              </p>
            </div>
          ) : isVideo && short.src ? (
            <video
              key={short.id}
              ref={videoRef}
              src={short.src}
              autoPlay
              muted={muted}
              playsInline
              className="w-full h-full object-cover"
              data-testid="shorts-video"
              onTimeUpdate={(e) => { const v = e.target; if (v.duration) setProgress(v.currentTime / v.duration); }}
              onEnded={() => { if (short.quiz && !quizOn) { setQuizOn(true); setProgress(1); } else goShort(idx + 1); }}
            />
          ) : (
            <ShortScene scene={short.scene} phase={step} />
          )}
        </div>
        <div className={`absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/60 pointer-events-none transition-opacity duration-500 ${!uiVisible ? 'opacity-0' : isYoutube ? 'opacity-40' : 'opacity-100'}`} />

        {/* Barres de progression (stories) */}
        <div className="absolute top-3 inset-x-3 flex gap-1.5 z-20">
          {short.steps.map((_, i) => (
            <div key={i} className="flex-1 h-[3px] rounded-full bg-white/25 overflow-hidden">
              <div
                className="h-full bg-white rounded-full"
                style={{ width: i < step ? '100%' : i === step ? `${progress * 100}%` : '0%' }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className={`absolute top-7 inset-x-0 px-4 flex items-center justify-between z-20 ${uiCls}`}>
          <button type="button" onClick={() => navigate(-1)} data-testid="shorts-back"
            className="w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/20 text-white flex items-center justify-center">
            <ArrowLeft size={16} />
          </button>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur border border-white/20 text-white text-[11px] font-bold tracking-widest font-mono-spec">
            <Clapperboard size={12} className="text-red-400" /> SHORTS · {idx + 1}/{feed.length}
          </span>
          <div className="flex items-center gap-2">
            {(isVideo || ytControllable) && (
              <button type="button" data-testid="shorts-mute"
                onClick={() => {
                  const next = !muted;
                  localStorage.setItem('cv_shorts_sound', next ? '0' : '1');
                  setMuted(next);
                  if (isVideo && videoRef.current) videoRef.current.muted = next;
                }}
                className={`h-9 rounded-full backdrop-blur border flex items-center justify-center gap-1.5 px-3 text-[11px] font-bold transition ${muted ? 'bg-red-600 border-red-400 text-white animate-pulse' : 'bg-black/50 border-white/20 text-white'}`}>
                {muted ? <><VolumeX size={14} /> SON</> : <Volume2 size={14} />}
              </button>
            )}
            {(!isYoutube || ytControllable) && short.type !== 'external' && (
              <button type="button" onClick={togglePause} data-testid="shorts-pause"
                className="w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/20 text-white flex items-center justify-center">
                {paused ? <Play size={14} /> : <Pause size={14} />}
              </button>
            )}
          </div>
        </div>

        {/* Zones tactiles — désactivées sur YouTube et liens externes pour laisser les clics passer */}
        {!isYoutube && short.type !== 'external' && (
          <>
            <button type="button" aria-label="Précédent" data-testid="shorts-tap-prev"
              className="absolute left-0 top-16 bottom-40 w-1/3 z-10"
              onClick={() => { if (isVideo && videoRef.current) videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10); else goStep(step - 1); }} />
            <button type="button" aria-label="Suivant" data-testid="shorts-tap-next"
              className="absolute right-0 top-16 bottom-40 w-2/3 z-10"
              onClick={() => { if (isVideo && videoRef.current) videoRef.current.currentTime = Math.min(videoRef.current.duration || 0, videoRef.current.currentTime + 10); else goStep(step + 1); }} />
          </>
        )}

        {/* Couche de réaffichage : un simple toucher fait revenir l'interface */}
        {!uiVisible && (
          <button type="button" aria-label="Afficher les infos" data-testid="shorts-reveal-ui"
            className="absolute inset-0 z-30 bg-transparent" onClick={revealUi} />
        )}

        {/* Navigation shorts (droite) */}
        <div className={`absolute right-3 bottom-44 z-30 flex flex-col gap-2 ${uiCls}`}>
          <button type="button" onClick={share} data-testid="shorts-share-btn" aria-label="Partager ce short"
            className="w-10 h-10 rounded-full bg-black/50 backdrop-blur border border-white/20 text-white flex items-center justify-center active:scale-90 transition hover:bg-white/10">
            <Share2 size={16} />
          </button>
          <Link to="/shorts/proposer" data-testid="shorts-propose-btn" aria-label="Proposer un short"
            className="hidden md:flex w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 border border-sky-300/50 text-white items-center justify-center active:scale-90 transition shadow-lg shadow-sky-900/40">
            <Plus size={18} strokeWidth={2.5} />
          </Link>
          <button type="button" onClick={() => goShort(idx - 1)} data-testid="shorts-prev"
            className="w-10 h-10 rounded-full bg-black/50 backdrop-blur border border-white/20 text-white flex items-center justify-center active:scale-90 transition">
            <ChevronUp size={18} />
          </button>
          <button type="button" onClick={() => goShort(idx + 1)} data-testid="shorts-next"
            className="w-10 h-10 rounded-full bg-black/50 backdrop-blur border border-white/20 text-white flex items-center justify-center active:scale-90 transition">
            <ChevronDown size={18} />
          </button>
        </div>

        {/* Quiz flash de fin de short */}
        {quizOn && short.quiz && (
          <div className="absolute inset-0 z-40 bg-black/85 backdrop-blur-sm flex items-end" data-testid="shorts-quiz-overlay">
            <div className="w-full p-5 pb-8">
              <div className="text-red-400 text-[10px] uppercase tracking-[0.25em] font-bold font-mono-spec mb-2">Question flash · ancre la règle</div>
              <h2 className="font-serif-editorial text-white text-[20px] leading-snug" data-testid="shorts-quiz-question">{short.quiz.q}</h2>
              <div className="mt-4 space-y-2">
                {short.quiz.options.map((opt, i) => {
                  const chosen = quizSel !== null;
                  const good = i === short.quiz.answer;
                  return (
                    <button key={i} type="button" disabled={chosen} onClick={() => setQuizSel(i)} data-testid={`shorts-quiz-option-${i}`}
                      className={`w-full text-left px-4 py-3 rounded-xl border text-[13.5px] font-semibold transition ${
                        !chosen ? 'bg-white/[0.06] border-white/15 text-white active:scale-[0.98]'
                        : good ? 'bg-emerald-600/25 border-emerald-500 text-emerald-200'
                        : i === quizSel ? 'bg-red-600/25 border-red-500 text-red-200'
                        : 'bg-white/[0.03] border-white/10 text-white/35'
                      }`}>
                      {opt}
                    </button>
                  );
                })}
              </div>
              {quizSel !== null && (
                <div className="mt-3 animate-fadeIn" data-testid="shorts-quiz-feedback">
                  <p className="text-white/80 text-[13px] leading-snug">
                    <span className={quizSel === short.quiz.answer ? 'text-emerald-400 font-bold' : 'text-red-400 font-bold'}>
                      {quizSel === short.quiz.answer ? 'Exact ! ' : 'Raté… '}
                    </span>
                    {short.quiz.explain}
                  </p>
                  <button type="button" onClick={() => goShort(idx + 1)} data-testid="shorts-quiz-next"
                    className="mt-4 w-full h-11 rounded-full bg-white text-black text-[13.5px] font-bold active:scale-95 transition">
                    Short suivant ↓
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bouton flottant mobile : proposer son short */}
        <Link to="/shorts/proposer" data-testid="shorts-propose-fab" aria-label="Proposer mon short"
          className={`md:hidden absolute right-3 bottom-3 z-30 inline-flex items-center gap-1.5 h-11 pl-3 pr-4 rounded-full bg-gradient-to-r from-sky-500 to-sky-700 border border-sky-300/50 text-white text-[12.5px] font-bold shadow-lg shadow-sky-900/50 active:scale-95 ${uiCls}`}>
          <Plus size={16} strokeWidth={2.75} /> Mon short
        </Link>

        {/* Texte de la leçon */}
        <div className={`absolute bottom-0 inset-x-0 p-4 pb-5 z-20 ${uiCls}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2.5 py-1 rounded-full text-white text-[10px] font-bold tracking-widest font-mono-spec" style={{ backgroundColor: short.color }}>
              {short.theme.toUpperCase()}
            </span>
            <span className="px-2.5 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white/85 text-[10px] font-bold tracking-wider">
              {short.kind}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white/85 text-[10px] font-bold" data-testid="shorts-views">
              <Eye size={10} /> {fmtViews(views[short.id] || 0)}
            </span>
          </div>
          <h1 className="font-serif-editorial text-white text-[22px] leading-tight" data-testid="shorts-title">{short.title}</h1>
          <p key={`${idx}-${step}`} className={`text-white/90 text-[14px] leading-snug mt-2 animate-fadeIn ${isVideo || isYoutube ? '' : 'min-h-[60px]'}`} data-testid="shorts-step-text">
            {short.steps[isVideo || isYoutube ? 0 : step]}
          </p>
          <Link to={short.course_to} data-testid="shorts-cta-course"
            className="mt-3 inline-flex items-center gap-2 h-10 px-5 rounded-full bg-white text-black text-[13px] font-bold active:scale-95 transition">
            {short.community ? <><Users size={14} /> Proposer mon short aussi</>
              : short.cta_label ? <><BookOpen size={14} /> {short.cta_label}</>
              : <><BookOpen size={14} /> Approfondir le cours complet</>}
          </Link>
        </div>
      </div>

      {/* Hint desktop */}
      <div className="hidden md:block absolute bottom-6 left-1/2 -translate-x-1/2 text-white/35 text-[11.5px] font-mono-spec">
        ↑↓ changer de short · ←→ étapes · espace pause
      </div>
    </div>
  );
}
